import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { CaretDown, MapPin, Info } from "@phosphor-icons/react";

/**
 * CityAutocomplete — dropdown con le 100+ città capoluogo OMI supportate.
 *
 * Avverte l'agente in tempo reale se sta scrivendo un quartiere noto come
 * città (es. "Brera" → "Brera è un quartiere di Milano. Usa Milano come
 * città e Brera nel campo Quartiere/Indirizzo"). Questo previene il bug
 * iter207 dove la stima cadeva 3-4× più bassa per via del fallback nazionale.
 */

// Mappa quartiere → città (allineata col backend NEIGHBORHOOD_TO_CITY)
const NEIGHBORHOOD_HINTS = {
  brera: "Milano", "porta nuova": "Milano", garibaldi: "Milano",
  cordusio: "Milano", cadorna: "Milano", duomo: "Milano",
  navigli: "Milano", "porta romana": "Milano", "porta venezia": "Milano",
  "porta genova": "Milano", isola: "Milano", "citta studi": "Milano",
  lambrate: "Milano", bicocca: "Milano", qt8: "Milano", "san siro": "Milano",
  trastevere: "Roma", prati: "Roma", parioli: "Roma", monti: "Roma",
  testaccio: "Roma", "san giovanni": "Roma", ostiense: "Roma",
  garbatella: "Roma", appio: "Roma", eur: "Roma", ostia: "Roma",
  "centro storico": "Roma",
  vomero: "Napoli", chiaia: "Napoli", posillipo: "Napoli", fuorigrotta: "Napoli",
  crocetta: "Torino", "san salvario": "Torino", vanchiglia: "Torino",
  "santo stefano": "Bologna", "san vitale": "Bologna",
  "santa croce": "Firenze", "san frediano": "Firenze", oltrarno: "Firenze",
  albaro: "Genova", carignano: "Genova", nervi: "Genova",
  "san marco": "Venezia", cannaregio: "Venezia", dorsoduro: "Venezia",
};

// Raggruppamento città per regione — usato per il dropdown quando il campo
// è vuoto, così le 171 città non sono un muro illegibile.
const REGION_GROUPS = [
  { region: "Lombardia", cities: ["Milano", "Bergamo", "Brescia", "Como", "Bellagio", "Cernobbio", "Tremezzo", "Menaggio", "Lecco", "Cremona", "Mantova", "Pavia", "Lodi", "Sondrio", "Livigno", "Bormio", "Sirmione", "Desenzano del Garda", "Salò", "Limone sul Garda", "Iseo", "Varese"] },
  { region: "Lazio", cities: ["Roma", "Latina", "Viterbo", "Frosinone", "Rieti"] },
  { region: "Campania", cities: ["Napoli", "Salerno", "Sorrento", "Positano", "Amalfi", "Ravello", "Capri", "Anacapri", "Ischia", "Forio", "Vico Equense", "Avellino", "Benevento", "Caserta"] },
  { region: "Liguria", cities: ["Genova", "Sanremo", "Alassio", "Bordighera", "Ventimiglia", "Diano Marina", "Rapallo", "Santa Margherita Ligure", "Portofino", "Sestri Levante", "Lerici", "Savona", "Imperia", "La Spezia"] },
  { region: "Toscana", cities: ["Firenze", "Pisa", "Siena", "Arezzo", "Lucca", "Livorno", "Grosseto", "Pistoia", "Forte dei Marmi", "Viareggio", "Pietrasanta", "Camaiore", "Marina di Pietrasanta", "Cortona", "San Gimignano", "Monteriggioni"] },
  { region: "Emilia-Romagna", cities: ["Bologna", "Modena", "Parma", "Reggio Emilia", "Rimini", "Ravenna", "Riccione", "Cattolica", "Cervia", "Milano Marittima", "Cesenatico", "Forlì", "Cesena", "Ferrara", "Piacenza"] },
  { region: "Veneto", cities: ["Venezia", "Verona", "Padova", "Treviso", "Vicenza", "Belluno", "Rovigo", "Jesolo", "Caorle", "Bibione"] },
  { region: "Piemonte", cities: ["Torino", "Novara", "Alessandria", "Asti", "Cuneo", "Biella", "Vercelli", "Sestriere", "Stresa", "Verbania"] },
  { region: "Sicilia", cities: ["Palermo", "Catania", "Messina", "Siracusa", "Trapani", "Agrigento", "Ragusa", "Taormina", "Cefalù", "Noto"] },
  { region: "Sardegna", cities: ["Cagliari", "Sassari", "Olbia", "Porto Cervo", "Porto Rotondo", "Arzachena", "Alghero", "Stintino"] },
  { region: "Trentino-Alto Adige", cities: ["Trento", "Bolzano", "Madonna di Campiglio", "Ortisei", "San Martino di Castrozza", "Selva di Val Gardena"] },
  { region: "Friuli-Venezia Giulia", cities: ["Trieste", "Udine", "Pordenone", "Gorizia", "Lignano Sabbiadoro", "Grado"] },
  { region: "Marche", cities: ["Ancona", "Pesaro", "Macerata", "Ascoli Piceno", "Fermo", "Senigallia"] },
  { region: "Umbria", cities: ["Perugia", "Terni", "Panicale"] },
  { region: "Abruzzo", cities: ["Pescara", "L'Aquila", "Teramo", "Chieti"] },
  { region: "Molise", cities: ["Campobasso", "Isernia"] },
  { region: "Puglia", cities: ["Bari", "Lecce", "Brindisi", "Taranto", "Foggia", "Barletta", "Andria", "Trani"] },
  { region: "Basilicata", cities: ["Potenza", "Matera"] },
  { region: "Calabria", cities: ["Reggio Calabria", "Cosenza", "Catanzaro", "Crotone", "Vibo Valentia"] },
  { region: "Valle d'Aosta", cities: ["Aosta", "Courmayeur", "Cervinia"] },
];

const _slug = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[àáâã]/g, "a")
    .replace(/[èéê]/g, "e")
    .replace(/[ìí]/g, "i")
    .replace(/[òóô]/g, "o")
    .replace(/[ùú]/g, "u")
    .replace(/['`\-_/]/g, " ")
    .replace(/\bnell\b\s*/g, " ")
    .replace(/\bdell\b\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function CityAutocomplete({
  value,
  onChange,
  placeholder = "Es. Milano",
  testId = "city-autocomplete",
  className = "",
  required = false,
}) {
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [expandedRegion, setExpandedRegion] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch cities once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/estimate/cities");
        if (alive && Array.isArray(data?.cities)) setCities(data.cities);
      } catch { /* offline → empty list, input still works */ }
    })();
    return () => { alive = false; };
  }, []);

  // Click-outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const slugValue = _slug(value);

  // Suggestions: max 8, ordered by prefix-match first.
  // Solo quando l'utente ha digitato qualcosa.
  const suggestions = useMemo(() => {
    if (!slugValue) return []; // empty → use REGION_GROUPS view instead
    const prefix = [];
    const contains = [];
    for (const c of cities) {
      const sc = _slug(c);
      if (sc.startsWith(slugValue)) prefix.push(c);
      else if (sc.includes(slugValue)) contains.push(c);
      if (prefix.length >= 8) break;
    }
    return [...prefix, ...contains].slice(0, 8);
  }, [cities, slugValue]);

  const showRegionView = open && !slugValue;
  const showSuggestionsView = open && slugValue && suggestions.length > 0;

  // Detect quartiere → città
  const neighborhoodHint = useMemo(() => {
    if (!slugValue) return null;
    const parent = NEIGHBORHOOD_HINTS[slugValue];
    if (parent) return { parent, raw: value };
    return null;
  }, [slugValue, value]);

  const pick = (city) => {
    onChange(city);
    setOpen(false);
    setHighlight(0);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && suggestions[highlight]) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin
          size={14}
          weight="duotone"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8B6E] pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          spellCheck="false"
          className="w-full pl-9 pr-9 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E] bg-white"
          data-testid={testId}
        />
        <CaretDown
          size={11}
          weight="bold"
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-[#9A968B] pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {/* Hint: quartiere → città */}
      {neighborhoodHint && !open && (
        <div
          className="mt-1.5 flex items-start gap-1.5 text-[11.5px] text-[#C8A66B] leading-snug"
          data-testid={`${testId}-hint`}
        >
          <Info size={12} weight="fill" className="mt-0.5 shrink-0" />
          <span>
            <strong>{neighborhoodHint.raw}</strong> è un quartiere di <strong>{neighborhoodHint.parent}</strong>.
            <button
              type="button"
              onClick={() => pick(neighborhoodHint.parent)}
              className="ml-1 text-[#1C1C1A] underline underline-offset-2 hover:text-[#7C8B6E]"
              data-testid={`${testId}-hint-fix`}
            >
              Usa {neighborhoodHint.parent}
            </button>
          </span>
        </div>
      )}

      {/* Suggestions dropdown — quando l'utente sta digitando */}
      {showSuggestionsView && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E5E7E0] rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-list`}
        >
          {suggestions.map((c, i) => (
            <button
              type="button"
              key={c}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 ${
                i === highlight ? "bg-[#F1F4ED] text-[#1C1C1A]" : "text-[#1C1C1A] hover:bg-[#FAFAF7]"
              }`}
              data-testid={`${testId}-option-${_slug(c)}`}
            >
              <MapPin size={11} weight="duotone" className="text-[#9A968B] shrink-0" />
              <span>{c}</span>
            </button>
          ))}
        </div>
      )}

      {/* Region groups view — quando l'utente apre senza scrivere.
          171 città suddivise in 20 regioni accordion. Mostra solo le regioni;
          click → espande la lista delle città di quella regione. */}
      {showRegionView && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E5E7E0] rounded-lg shadow-lg max-h-80 overflow-y-auto"
          data-testid={`${testId}-region-list`}
        >
          <div className="px-3 py-2 text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] border-b border-[#EAE7DF] bg-[#FAFAF7]">
            Sfoglia per regione · {cities.length || "171"} città
          </div>
          {REGION_GROUPS.map(({ region, cities: regionCities }) => {
            const isOpen = expandedRegion === region;
            return (
              <div key={region} className="border-b border-[#EAE7DF] last:border-0">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setExpandedRegion(isOpen ? null : region);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left text-[13px] font-medium text-[#1C1C1A] hover:bg-[#F1F4ED] transition-colors"
                  data-testid={`${testId}-region-${_slug(region)}`}
                >
                  <span className="flex items-center gap-2">
                    <span>{region}</span>
                    <span className="text-[10.5px] text-[#9A968B] font-normal">
                      {regionCities.length}
                    </span>
                  </span>
                  <CaretDown
                    size={11}
                    weight="bold"
                    className={`text-[#9A968B] transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="bg-[#FAFAF7] py-1">
                    {regionCities.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                        className="w-full text-left px-7 py-1.5 text-[12.5px] text-[#3A3A36] hover:bg-white hover:text-[#1C1C1A] flex items-center gap-2"
                        data-testid={`${testId}-option-${_slug(c)}`}
                      >
                        <MapPin size={10} weight="duotone" className="text-[#9A968B] shrink-0" />
                        <span>{c}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
